import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import AgGrid from "../components/grid/AgGrid";
import { leftAlignColDef, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";

const StageBadgeRenderer = (params) => {
  const stage = params.value;

  let color = "#64748b";
  let bg = "rgba(100,116,139,0.1)";
  let border = "1px solid rgba(100,116,139,0.2)";

  const s = stage?.toUpperCase() || "";
  
  if (s.startsWith("A1")) {
    color = "#ef4444"; // Red
    bg = "rgba(239,68,68,0.1)";
    border = "1px solid rgba(239,68,68,0.2)";
  } else if (s.startsWith("A2")) {
    color = "#f59e0b"; // Orange
    bg = "rgba(245,158,11,0.1)";
    border = "1px solid rgba(245,158,11,0.2)";
  } else if (s.startsWith("A3")) {
    color = "#0ea5e9"; // Blue
    bg = "rgba(14,165,233,0.1)";
    border = "1px solid rgba(14,165,233,0.2)";
  } else if (s.startsWith("A4") || s.startsWith("D") || s.includes("AVOID")) {
    color = "#22c55e"; // Green
    bg = "rgba(34,197,94,0.1)";
    border = "1px solid rgba(34,197,94,0.2)";
  } else if (s.startsWith("A5") || s.includes("BIOGENIC")) {
    color = "#8b5cf6"; // Purple
    bg = "rgba(139,92,246,0.1)";
    border = "1px solid rgba(139,92,246,0.2)";
  } else if (s.startsWith("B")) {
    color = "#14b8a6"; // Teal
    bg = "rgba(20,184,166,0.1)";
    border = "1px solid rgba(20,184,166,0.2)";
  } else if (s.startsWith("C")) {
    color = "#ec4899"; // Pink
    bg = "rgba(236,72,153,0.1)";
    border = "1px solid rgba(236,72,153,0.2)";
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "1",
        color,
        backgroundColor: bg,
        border,
        whiteSpace: "nowrap"
      }}
    >
      {stage}
    </div>
  );
};

const ProcessSummaryTable = ({ processData = [], grossTotal = 0, isDark = false, hidePagination = false }) => {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    return processData.map((row) => ({
      ...row,
      pct: grossTotal > 0 ? ((Math.abs(row.gwp) / grossTotal) * 100).toFixed(2) : "0.00"
    }));
  }, [processData, grossTotal]);

  const columnDefs = useMemo(
    () => [
      {
        field: "lca_stage",
        headerName: t("LCA Stage"),
        ...leftAlignColDef,
        width: 280,
        cellRenderer: StageBadgeRenderer,
        resizable: true,
        sortable: true
      },
      {
        field: "name",
        headerName: t("Process Name"),
        ...leftAlignColDef,
        flex: 1,
        minWidth: 250,
        resizable: true,
        sortable: true
      },
      {
        field: "gwp",
        headerName: t(`Emissions (kg CO₂e)`),
        ...rightAlignColDef,
        width: 250,
        valueFormatter: (p) => p.value ? Number(p.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00",
        resizable: true,
        sortable: true
      },
      {
        field: "pct",
        headerName: t("% of Total"),
        ...rightAlignColDef,
        width: 130,
        valueFormatter: (p) => `${p.value}%`,
        resizable: true,
        sortable: true
      }
    ],
    [t]
  );

  return (
    <AgGrid
      columnDefs={columnDefs}
      rowData={rows}
      getRowId={(params) => params.data.id}
      domLayout="autoHeight"
      suppressRowClickSelection
      headerHeight={48}
      rowHeight={48}
      sx={{
        "& .ag-row-first": {
          borderTop: "none !important",
        }
      }}
      suppressPagination={hidePagination}
    />
  );
};

export default ProcessSummaryTable;
